import { useCallback } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import { encodeRovoParams } from '@atlaskit/rovo-triggers';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation';

import {
	getAtlassianStudioAgentDuplicateUrl,
	getAtlassianStudioAgentEditUrl,
} from '../../common/utils/atlassian-studio-url';
import { useAtlasURL } from '../atlas-url';

export const useAgentUrlActions = ({ siteId }: { siteId: string }) => {
	const { atlasURL } = useAtlasURL(siteId);
	const { sendAnalyticsEvent } = useAnalytics();

	const onEditAgent = useCallback(
		(agentId: string) => {
			if (fg('redirect_rovo_to_agent_studio_enabled')) {
				window.open(getAtlassianStudioAgentEditUrl(siteId, agentId));
			} else {
				window.open(atlasURL(`/chat/agents/${agentId}/edit`), '_blank', 'noopener, noreferrer');
			}
		},
		[atlasURL, siteId],
	);

	const onCopyAgent = useCallback(
		({
			agentId,
			agentIdentityAccountId,
		}: {
			agentId: string;
			agentIdentityAccountId?: string | null;
		}) => {
			if (agentIdentityAccountId) {
				navigator.clipboard.writeText(`${window.location.origin}/people/agent/${agentId}`);
				return;
			}

			const rovoParams = encodeRovoParams({
				pathway: 'chat',
				agentId: agentId,
			});
			navigator.clipboard.writeText(atlasURL(`/chat?${rovoParams}`));
		},
		[atlasURL],
	);

	const onDuplicateAgent = useCallback(
		(agentId: string, source: string) => {
			if (fg('redirect_rovo_to_agent_studio_enabled')) {
				window.open(getAtlassianStudioAgentDuplicateUrl(siteId, agentId));
			} else {
				const rovoParams = encodeRovoParams({
					pathway: 'agents-create',
					agentId: agentId,
				});
				window.open(atlasURL(`/chat/agents/new?${rovoParams}`), '_blank', 'noopener, noreferrer');
			}
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'duplicateAgentButton',
				attributes: { agentId, source },
			});
		},
		[atlasURL, sendAnalyticsEvent, siteId],
	);

	const onViewAgentFullProfile = useCallback((agentId: string) => {
		window.open(
			`${window.location.origin}/people/agent/${agentId}`,
			'_blank',
			'noopener, noreferrer',
		);
	}, []);

	const onStartNewChatWindowWithPrompt = useCallback(
		(agentId: string, prompt: string) => {
			const rovoParams = encodeRovoParams({
				pathway: 'chat',
				agentId: agentId,
				prompt,
			});
			window.open(atlasURL(`/chat?${rovoParams}`), '_blank', 'noopener, noreferrer');
		},
		[atlasURL],
	);

	return {
		onEditAgent,
		onCopyAgent,
		onDuplicateAgent,
		onViewAgentFullProfile,
		onStartNewChatWindowWithPrompt,
	};
};
